import { useQuery } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';
import { PropsWithChildren, useCallback, useEffect } from 'react';

import { getUserRegionApi } from '@flipster/apis/v1/user';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useIsWebview } from '@flipster/features/src/shared/webview';
import { usePathname } from '@flipster/i18n/routing';
import { alert } from '@flipster/ui/Alert';
import segment from '@flipster/utils/segment';
import { RestrictedRegionEvent } from '@flipster/utils/segment/events/restricted-region';

import { restrictedCountryBunja } from '@web/app/[locale]/(web)/(session)/_bunja/restricted-country-state';

import { useTemporaryAllowPage } from './useTemporaryAllowPage';

export const useBlockedRegion = () => {
  const router = useRouter();
  const pathname = usePathname();

  const isWebview = useIsWebview();
  const isTemporarilyAllow = useTemporaryAllowPage();

  const { data: isRestrictedRegion = false } = useQuery({
    queryKey: ['getUserRegionApi', 'isRestrictedRegion'],
    queryFn: getUserRegionApi,
    select: (res) => res.data.isRestrictedRegion,
    enabled: !isTemporarilyAllow && !isWebview,
    refetchOnWindowFocus: false,
  });

  const regionAlert = useCallback(
    (targetPath?: string) => {
      alert.show({
        title: 'Restricted location',
        message: (
          <div>
            Flipster isn’t available in your current location due to regulatory restrictions. If you’re just visiting,
            let us know your nationality to continue.
          </div>
        ),
        confirmText: 'Got It',
        closeText: "I'm just visiting",
        withClose: true,
        onConfirm: () => {
          router.push('/');
          segment.track(RestrictedRegionEvent.RestrictedLocationPopupClickGotIt);
        },
        onClose: () => {
          const returnUrl = encodeURIComponent(targetPath ?? pathname);
          router.push(`/select-nationality?returnUrl=${returnUrl}`);
          segment.track(RestrictedRegionEvent.RestrictedLocationPopupClickVisiting);
        },
      });
      segment.track(RestrictedRegionEvent.RestrictedLocationPopupView);
    },
    [router, pathname],
  );

  return { isRestrictedRegion, regionAlert };
};

export const RegionBlocker = ({ children }: PropsWithChildren) => {
  const { isRestrictedRegion, regionAlert } = useBlockedRegion();
  const { selectedCountryAtom } = useBunja(restrictedCountryBunja);
  const selectedCountry = useAtomValue(selectedCountryAtom);
  const { isCookieLoggedIn } = useAuth();

  useEffect(() => {
    if (isRestrictedRegion && !selectedCountry && !isCookieLoggedIn) {
      regionAlert();
    }
  }, [isRestrictedRegion, regionAlert, selectedCountry, isCookieLoggedIn]);

  return children;
};
